import { useState } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import { BuscadorCliente, type ClienteElegido } from '@/modules/comercial/BuscadorCliente'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Modal } from '@/shared/ui/Modal/Modal'

/**
 * CAMBIARLE EL CLIENTE A UNA ORDEN.
 *
 * El cliente se fijaba al crear la orden y no había forma de corregirlo: ni en
 * la pantalla ni en la base. Y hace falta más de lo que parece, porque el
 * técnico en la calle levanta el trabajo a nombre de CONSUMIDOR FINAL —el
 * mostrador— y el cliente real se sabe después, cuando pide la factura.
 *
 * Desde ahora eso importa: a CONSUMIDOR FINAL no se le manda la cotización
 * porque no tiene teléfono, así que sin esta puerta la orden se quedaba
 * atascada sin poder avanzar.
 *
 * El cliente se busca en el catálogo de World Office —no en nuestra matriz—:
 * si allá existe y aquí no, se adopta en el momento, que es lo mismo que hace
 * el formulario de una orden nueva.
 */
export function CambiarClienteDeLaOrden({
  ordenServicioId,
  clienteActual,
  abierto,
  onCerrar,
}: {
  ordenServicioId: string
  clienteActual: string
  abierto: boolean
  onCerrar: () => void
}) {
  const clienteConsultas = useQueryClient()
  const [elegido, setElegido] = useState<ClienteElegido | null>(null)
  const [error, setError] = useState<string | null>(null)

  const cambiar = useMutation({
    mutationFn: async (tercero: ClienteElegido) => {
      // Primero se asegura de que exista de este lado; devuelve su id.
      const { data: clienteId, error: falloAdopcion } = await supabase.rpc('fn_adoptar_cliente', {
        p_id_tercero: tercero.id_tercero,
      })

      if (falloAdopcion) throw new Error(mensajeDeError(falloAdopcion))

      const { error: fallo } = await supabase.rpc('fn_cambiar_cliente_de_la_orden', {
        p_orden_servicio_id: ordenServicioId,
        p_cliente_id: clienteId as string,
        p_motivo: null,
      })

      if (fallo) throw new Error(mensajeDeError(fallo))
    },
    onSuccess: async () => {
      await clienteConsultas.invalidateQueries({ queryKey: ['ordenes-servicio'] })
      await clienteConsultas.invalidateQueries({ queryKey: ['bitacora'] })
      setElegido(null)
      onCerrar()
    },
  })

  async function confirmar() {
    if (!elegido) return
    setError(null)

    try {
      await cambiar.mutateAsync(elegido)
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo cambiar el cliente.')
    }
  }

  return (
    <Modal
      abierto={abierto}
      titulo="Cambiar el cliente de la orden"
      subtitulo={`Ahora está a nombre de ${clienteActual}`}
      onCerrar={onCerrar}
      pie={
        <>
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton
            variante="principal"
            onClick={() => void confirmar()}
            trabajando={cambiar.isPending}
            disabled={!elegido}
          >
            Cambiar el cliente
          </Boton>
        </>
      }
    >
      {error && <Aviso tipo="error">{error}</Aviso>}

      <BuscadorCliente
        etiqueta="Cliente real"
        elegido={elegido}
        onElegir={setElegido}
        ayuda="Búsquelo en World Office. Si no existe, se puede crear aquí mismo."
      />

      {/* LO QUE ESTE CAMBIO NO HACE, dicho antes y no después. El documento de
          World Office ya nació a nombre del tercero viejo; cambiarlo aquí no
          lo cambia allá. */}
      <Aviso tipo="info">
        Esto cambia el cliente de la orden en la aplicación. Si la cotización ya
        está en World Office, allá sigue a nombre del anterior: para moverla
        también hay que <b>modificar la cotización</b>, que es cuando se le
        manda el cliente nuevo.
      </Aviso>
    </Modal>
  )
}
